"use client";

import { motion, type HTMLMotionProps, type Variants } from "motion/react";
import type { ReactNode } from "react";

import { useUncovered } from "./stage";

// =============================================================================

export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
export const EASE_IN_OUT = [0.76, 0, 0.24, 1] as const;

const VIEWPORT = { once: true, margin: "0px 0px -12% 0px" } as const;

// =============================================================================

type RevealProps = Omit<HTMLMotionProps<"div">, "children"> & {
  children: ReactNode;
  delay?: number;
  duration?: number;
  /** Vertical offset in px */
  y?: number;
  /** Horizontal offset in px */
  x?: number;
  blur?: boolean;
  /** Animate on mount instead of when entering the viewport */
  immediate?: boolean;
};

/**
 * Fades and slides its content in when it enters the viewport (never under a
 * page transition curtain).
 */
export function Reveal({
  children,
  delay = 0,
  duration = 0.9,
  y = 32,
  x = 0,
  blur = false,
  immediate = false,
  ...props
}: RevealProps) {
  const hidden = {
    opacity: 0,
    y,
    x,
    ...(blur && { filter: "blur(8px)" }),
  };
  const visible = {
    opacity: 1,
    y: 0,
    x: 0,
    ...(blur && { filter: "blur(0px)" }),
  };
  const uncovered = useUncovered();

  return (
    <motion.div
      initial={hidden}
      {...(immediate
        ? { animate: uncovered ? visible : hidden }
        : uncovered && { whileInView: visible, viewport: VIEWPORT })}
      transition={{ duration, delay, ease: EASE_OUT_EXPO }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// =============================================================================

const groupVariants = (stagger: number, delay: number): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
});

export const revealItemVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE_OUT_EXPO },
  },
};

type RevealGroupProps = Omit<HTMLMotionProps<"div">, "children"> & {
  children: ReactNode;
  stagger?: number;
  delay?: number;
  immediate?: boolean;
};

/**
 * Staggers the entrance of its RevealItem children.
 */
export function RevealGroup({
  children,
  stagger = 0.08,
  delay = 0,
  immediate = false,
  ...props
}: RevealGroupProps) {
  const uncovered = useUncovered();

  return (
    <motion.div
      variants={groupVariants(stagger, delay)}
      initial="hidden"
      {...(immediate
        ? { animate: uncovered ? "visible" : "hidden" }
        : uncovered && { whileInView: "visible", viewport: VIEWPORT })}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function RevealItem({
  children,
  variants = revealItemVariants,
  ...props
}: Omit<HTMLMotionProps<"div">, "children"> & { children: ReactNode }) {
  return (
    <motion.div variants={variants} {...props}>
      {children}
    </motion.div>
  );
}

// =============================================================================
